import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import matchService, { MatchAttempt } from '../services/matchService';
import {
  ArrowLeftIcon,
  TrophyIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  SparklesIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface MatchCard {
  id: string;
  content: string;
  type: 'front' | 'back';
  pairId: string;
  isMatched: boolean;
  isSelected: boolean;
}

interface MatchGame {
  _id: string;
  title: string;
  description?: string;
  type: 'deck-based' | 'custom' | 'ai-generated';
  pairs: Array<{
    front: string;
    back: string;
  }>;
}

const MatchPlay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [game, setGame] = useState<MatchGame | null>(null);
  const [loading, setLoading] = useState(true);
  const [matchCards, setMatchCards] = useState<MatchCard[]>([]);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number>(0);
  const [attempts, setAttempts] = useState<number>(0);
  const [startTime, setStartTime] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [attemptHistory, setAttemptHistory] = useState<MatchAttempt[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentAttemptId, setCurrentAttemptId] = useState<string | null>(null);
  const [pairMatchTimes, setPairMatchTimes] = useState<Map<string, { matchedAt: Date; attempts: number }>>(new Map());

  useEffect(() => {
    loadGame();
  }, [id]);

  useEffect(() => {
    if (startTime > 0 && !gameComplete) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 100);
      return () => clearInterval(interval);
    }
  }, [startTime, gameComplete]);

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const stripHtml = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const loadGame = async () => {
    try {
      setLoading(true);
      const gameData = await matchService.getMatchGame(id!);
      
      setGame(gameData);
      initializeGame(gameData);
      await loadAttemptHistory();
    } catch (error) {
      console.error('Error loading match game:', error);
      toast.error('Erro ao carregar o jogo');
      navigate('/matches');
    } finally {
      setLoading(false);
    }
  };

  const loadAttemptHistory = async () => {
    try {
      const history = await matchService.getMatchAttempts(id!);
      setAttemptHistory(history);
    } catch (error) {
      console.error('Error loading attempt history:', error);
    }
  };

  const initializeGame = async (gameData: MatchGame) => {
    const cards: MatchCard[] = [];
    
    gameData.pairs.forEach((pair, index) => {
      const pairId = `pair-${index}`;
      cards.push({
        id: `${pairId}-front`,
        content: stripHtml(pair.front),
        type: 'front',
        pairId,
        isMatched: false,
        isSelected: false
      });
      cards.push({
        id: `${pairId}-back`,
        content: stripHtml(pair.back),
        type: 'back',
        pairId,
        isMatched: false,
        isSelected: false
      });
    });

    setMatchCards(shuffleArray(cards));
    setMatchedPairs(0);
    setAttempts(0);
    setStartTime(Date.now());
    setCurrentTime(Date.now());
    setGameComplete(false);
    setSelectedCards([]);
    setPairMatchTimes(new Map());

    // Start a new attempt
    try {
      const attempt = await matchService.startMatchAttempt(gameData._id);
      setCurrentAttemptId(attempt._id);
    } catch (error) {
      console.error('Error starting match attempt:', error);
      toast.error('Erro ao iniciar tentativa');
    }
  };

  const handleCardClick = (cardId: string) => {
    if (isChecking || selectedCards.length >= 2) return;
    if (selectedCards.includes(cardId)) return;
    
    const card = matchCards.find(c => c.id === cardId);
    if (!card || card.isMatched) return;

    const newSelectedCards = [...selectedCards, cardId];
    setSelectedCards(newSelectedCards);

    // Update card selection state
    setMatchCards(prev => prev.map(c => 
      c.id === cardId ? { ...c, isSelected: true } : c
    ));

    if (newSelectedCards.length === 2) {
      setIsChecking(true);
      setAttempts(prev => prev + 1);
      
      const card1 = matchCards.find(c => c.id === newSelectedCards[0]);
      const card2 = matchCards.find(c => c.id === newSelectedCards[1]);

      if (card1 && card2 && card1.pairId === card2.pairId) {
        // Match found!
        setTimeout(async () => {
          setMatchCards(prev => prev.map(c => 
            c.pairId === card1.pairId 
              ? { ...c, isMatched: true, isSelected: false } 
              : c
          ));
          
          // Track the pair match
          setPairMatchTimes(prev => {
            const newMap = new Map(prev);
            newMap.set(card1.pairId, {
              matchedAt: new Date(),
              attempts: Math.floor(attempts / 2) + 1
            });
            return newMap;
          });

          setMatchedPairs(prev => prev + 1);
          setSelectedCards([]);
          setIsChecking(false);

          // Check if game is complete
          const totalPairs = matchCards.length / 2;
          if (matchedPairs + 1 === totalPairs) {
            setGameComplete(true);
            await submitAttempt();
            toast.success('🎉 Parabéns! Você completou o jogo!');
          }
        }, 500);
      } else {
        // No match
        setTimeout(() => {
          setMatchCards(prev => prev.map(c => 
            newSelectedCards.includes(c.id) 
              ? { ...c, isSelected: false } 
              : c
          ));
          setSelectedCards([]);
          setIsChecking(false);
        }, 1000);
      }
    }
  };

  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const submitAttempt = async () => {
    if (!currentAttemptId) {
      console.error('No attempt ID to submit');
      return;
    }

    try {
      const timeSpentSeconds = Math.floor((Date.now() - startTime) / 1000);
      const pairMatches = Array.from(pairMatchTimes.entries()).map(([pairId, data]) => ({
        pairId,
        matchedAt: data.matchedAt.toISOString(),
        attemptsBeforeMatch: data.attempts
      }));

      await matchService.submitMatchAttempt(
        currentAttemptId,
        timeSpentSeconds,
        attempts,
        pairMatches
      );

      // Reload history after submission
      await loadAttemptHistory();
    } catch (error) {
      console.error('Error submitting attempt:', error);
      toast.error('Erro ao salvar tentativa');
    }
  };

  const resetGame = () => {
    if (game) {
      initializeGame(game);
    }
  };

  const elapsedTime = currentTime - startTime;
  const totalPairs = matchCards.length / 2;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!game) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-all duration-500">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="bg-gray-800 dark:bg-gray-800 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 shadow-xl">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/matches')}
              className="inline-flex items-center justify-center p-2 rounded-lg text-white hover:bg-gray-700 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            
            <div className="flex-1 hidden md:block">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
                {game.title}
              </h1>
            </div>
            
            <button
              onClick={resetGame}
              className="inline-flex items-center justify-center p-2 rounded-lg text-white hover:bg-gray-700 transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">Pares Encontrados</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {matchedPairs}/{totalPairs}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">Tempo</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {formatTime(elapsedTime)}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">Tentativas</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {attempts}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              <TrophyIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">Precisão</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {attempts > 0 ? Math.round((totalPairs / attempts) * 100) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Attempt History */}
        {attemptHistory.length > 0 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center space-x-2">
                <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-500" />
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  Histórico de Tentativas ({attemptHistory.length})
                </h2>
              </div>
              <span className="text-gray-500 dark:text-gray-400">
                {showHistory ? '▼' : '▶'}
              </span>
            </button>
            
            {showHistory && (
              <div className="mt-4 space-y-3">
                {attemptHistory.map((attempt, index) => (
                  <div
                    key={attempt._id}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                          #{attemptHistory.length - index}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <div className="flex items-center space-x-1">
                              <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 flex-shrink-0" />
                              <span className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {formatTime(attempt.timeSpent * 1000)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <SparklesIcon className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500 flex-shrink-0" />
                              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                {attempt.movesCount} mov.
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                            {new Date(attempt.completedAt || attempt.startedAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        {index === 0 && (
                          <span className="px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                            Recente
                          </span>
                        )}
                        {attempt.timeSpent === Math.min(...attemptHistory.map(a => a.timeSpent)) && (
                          <span className="px-2 py-1 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs font-bold rounded-full flex items-center space-x-1 whitespace-nowrap">
                            <TrophyIcon className="h-3 w-3" />
                            <span>Melhor</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Completion Modal */}
        {gameComplete && (
          <div className="bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8 text-white text-center shadow-2xl">
            <TrophyIcon className="h-16 w-16 sm:h-20 sm:w-20 mx-auto mb-4 drop-shadow-lg" />
            <h2 className="text-3xl sm:text-4xl font-bold mb-2 sm:mb-3 drop-shadow-md">🎉 Parabéns! 🎉</h2>
            <p className="text-xl sm:text-2xl mb-4 sm:mb-6">Você completou o jogo!</p>
            <div className="grid grid-cols-3 gap-3 sm:gap-6 text-base sm:text-xl bg-white/20 backdrop-blur-sm rounded-xl p-4 sm:p-6">
              <div>
                <div className="font-bold text-2xl sm:text-3xl">{attempts}</div>
                <div className="text-xs sm:text-sm opacity-90">Tentativas</div>
              </div>
              <div>
                <div className="font-bold text-2xl sm:text-3xl">{formatTime(elapsedTime)}</div>
                <div className="text-xs sm:text-sm opacity-90">Tempo</div>
              </div>
              <div>
                <div className="font-bold text-2xl sm:text-3xl">{Math.round((totalPairs / attempts) * 100)}%</div>
                <div className="text-xs sm:text-sm opacity-90">Precisão</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-4 sm:mt-6">
              <button
                onClick={resetGame}
                className="px-6 sm:px-8 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
              >
                Jogar Novamente
              </button>
              <button
                onClick={() => navigate('/matches')}
                className="px-6 sm:px-8 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all transform hover:scale-105 shadow-lg border-2 border-white/30"
              >
                Voltar aos Jogos
              </button>
            </div>
          </div>
        )}

        {/* Match Cards Grid */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-3 sm:p-4 lg:p-6">
          <div className={`grid gap-2 sm:gap-3 lg:gap-4 ${
            matchCards.length <= 8 
              ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' 
              : matchCards.length <= 12
              ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
              : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'
          }`}>
            {matchCards.map((card) => (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                disabled={card.isMatched || isChecking}
                className={`
                  relative p-3 sm:p-4 lg:p-6 rounded-xl border-2 transition-all duration-300 min-h-[100px] sm:min-h-[120px] flex items-center justify-center text-center font-medium
                  ${card.isMatched 
                    ? 'bg-green-100 dark:bg-green-900/30 border-green-500 opacity-60 cursor-not-allowed scale-95' 
                    : card.isSelected
                      ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-500 scale-105 shadow-xl ring-4 ring-purple-300 dark:ring-purple-700'
                      : isDark
                        ? 'bg-gray-700 border-gray-600 hover:border-pink-400 hover:scale-105 hover:shadow-xl cursor-pointer'
                        : 'bg-white border-gray-300 hover:border-pink-400 hover:scale-105 hover:shadow-xl cursor-pointer'
                  }
                  ${isChecking ? 'pointer-events-none' : ''}
                `}
              >
                {card.isMatched && (
                  <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                    <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 drop-shadow-md" />
                  </div>
                )}
                <span className={`text-xs sm:text-sm lg:text-base break-words ${
                  card.isMatched 
                    ? 'text-gray-500 dark:text-gray-400 line-through' 
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {card.content.length > 50 
                    ? card.content.substring(0, 50) + '...' 
                    : card.content
                  }
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchPlay;
